import { Time } from '@internationalized/date';
import React__default, { useCallback, useEffect } from 'react';
import { useTimeField, useDateSegment } from 'react-aria';
import { useTimeFieldState } from 'react-stately';
import styled from 'styled-components';
import { useLocale } from '../../lib/translate.js';
import { tokens } from '../../tokens.js';
import { ButtonIcon } from '../button/ButtonIcon.js';
import { __Icon } from '../icon/Icon.js';
import { Stack } from '../layout/Stack.js';
import { Text } from '../text/Text.js';
import { InputDescription } from './lib/InputDescription.js';

const cssvars = tokens.components.input;
const Input = styled.div`
  display: inline-flex;
  color: ${cssvars.color};
  background-color: ${cssvars["bg-color"]};
  border: 1px solid transparent;
  border-radius: ${cssvars["border-radius"]};
  font-size: ${cssvars["font-size"]};
  font-weight: ${cssvars["font-weight"]};
  font-family: inherit;
  min-height: ${cssvars.height};
  padding: 1.125rem 1rem 0.25rem 3rem;

  &::placeholder {
    color: ${cssvars.placeholder.color};
    font-weight: ${cssvars.placeholder["font-weight"]};
  }

  &:hover,
  &:focus,
  &:focus-within {
    border-color: ${cssvars.hover["border-color"]};
    outline: none;
  }

  /* replace the default autofill stylings */
  &:-webkit-autofill,
  &:-webkit-autofill:active {
    -webkit-box-shadow: 0 0 0 30px ${cssvars["inner-value"].focus["bg-color"]} inset;
    border: 1px solid transparent;
  }

  [data-hd-disabled='true'] & {
    cursor: default;
    pointer-events: none;
    color: ${cssvars.disabled.color};
  }

  [data-hd-readonly='true'] & {
    cursor: default;
    pointer-events: none;
  }

  [data-hd-error='true'] & {
    border-color: ${cssvars.error["border-color"]};
  }

  & .time-input-icon {
    position: absolute;
    height: 2rem;
    width: 2rem;
    top: 0.625rem;
    left: 0.625rem;
    color: ${cssvars.icon.color};
  }

  [data-hd-disabled='true'] & .time-input-icon {
    color: ${cssvars.disabled.color};
  }

  & .time-input-inner-label {
    pointer-events: none;
    opacity: 1;

    transition: transform 0.2s ease-in-out;
    transform: scale(0.75) translateX(0) translateY(0);
    transform-origin: top left;

    display: flex;
    white-space: nowrap;
    overflow: hidden;

    max-width: -webkit-fill-available;
    margin-left: 1px;
    margin-right: 1rem;
    padding-left: 2.75rem;
  }
`;
const InnerInputLabel = styled(Text)`
  color: ${cssvars.label.color};
  position: absolute;
  left: 1rem;
  top: 0.5rem;
  font-size: ${cssvars.label["font-size"]};
  font-weight: ${cssvars.label["font-weight"]};

  [data-hd-disabled='true'] & {
    cursor: default;
    pointer-events: none;
    color: ${cssvars.disabled.color};
  }
`;
const InputInnerText = styled.span`
  font-size: ${cssvars["inner-value"]["font-size"]};
  line-height: 1.5;
  font-weight: ${cssvars["font-weight"]};
  display: inline-flex;
  margin-left: -0.125rem;
  margin-bottom: 0.25rem;
  height: 1.5rem;
`;
const InputInnerSegment = styled.span`
  cursor: text;
  padding: 0.125rem;

  &:focus {
    outline: none;
    background-color: ${tokens.color.palette.gray[20]};
    border-radius: 0.125rem;
  }

  &[aria-hidden='true'] {
    padding: 0.125rem 0;
  }
`;
/**
 * The actual input for a Time Input. Segmented as the hours, minutes and am/pm of the time are separate typeable
 * inputs.
 */ const InputSegment = ({ segment , state  })=>{
    const ref = React__default.useRef(null);
    /**
     * Use react-aria to handle the binding of the seperate inputs for time elements with the overall time value.
     */ const ariaSegment = useDateSegment(segment, state, ref);
    return React__default.createElement(InputInnerSegment, {
        ...ariaSegment.segmentProps,
        ref: ref
    }, segment.text);
};
/**
 * Floating button within input that allows clearing the value.
 */ const ClearButton = styled(ButtonIcon)`
  position: absolute;
  right: 0.625rem;
  top: 0.625rem;
  /* override default button hover background to not show on input */
  background-color: transparent;
`;
/**
 * Allows the user to select and edit a specific time. Each part of a time value is displayed in an individually editable segment.
 * Time Inputs can be used as fully controlled components, or as part of a form using the wrapping `TimeField` component.
 *
 * @example
 * <TimeInput label="Scheduled" value={time} onChange={setTime} />
 */ const TimeInput = ({ testId ="X" , label , value , error , description , disabled =false , readOnly =false , reserveMessageSpace =false , onChange , onBlur , ...props })=>{
    /**
     * Converts the react-aria external Time API to the Happy Design API of leveraging base JS Date objects.
     */ const onChangeTime = useCallback((time)=>{
        /**
         * Clear the current time.
         */ if (time === undefined || time === null) {
            onChange?.(undefined);
            return;
        }
        /**
         * If the value has an associated date, we don't want to override the date portion, but we do want to override
         * the time. This allows composite use of the value with a Date Input or other shared datetime usecases.
         */ const datetime = value !== undefined ? new Date(value) : new Date();
        if (time.hour !== undefined) {
            datetime.setHours(time.hour, time.minute, time.second, time.millisecond);
        } else {
            datetime.setMinutes(time.minute, time.second, time.millisecond);
        }
        onChange?.(datetime);
    }, [
        onChange,
        value
    ]);
    const locale = useLocale();
    /**
     * Use the react-aria behavioural hook for time input functionality.
     * This handles the logic of invalid input and converting input to consumable values.
     * Can later handle timezone setting from within the input.
     */ const ariaState = useTimeFieldState({
        value: value !== undefined ? new Time(value.getHours(), value.getMinutes(), value.getSeconds()) : undefined,
        onChange: onChangeTime,
        locale,
        hideTimeZone: true,
        isDisabled: disabled,
        isReadOnly: readOnly
    });
    const onBlurTime = useCallback(()=>{
        const time = ariaState.value;
        /**
         * Clear the current time.
         */ if (time === undefined || time === null) {
            onBlur?.(undefined);
            return;
        }
        /**
         * If the value has an associated date, we don't want to override the date portion, but we do want to override
         * the time. This allows composite use of the value with a Date Input or other shared datetime usecases.
         */ const datetime = value !== undefined ? new Date(value) : new Date();
        if (time.hour !== undefined) {
            datetime.setHours(time.hour, time.minute, time.second, time.millisecond);
        } else {
            datetime.setMinutes(time.minute, time.second, time.millisecond);
        }
        onBlur?.(datetime);
    }, [
        ariaState.value,
        onBlur,
        value
    ]);
    const inputRef = React__default.useRef(null);
    /**
     * Binds the react-aria behaviours to actual dom elements, handling keyboard navigation, focus and value updates.
     */ const ariaTime = useTimeField({
        value: value !== undefined ? new Time(value.getHours(), value.getMinutes(), value.getSeconds()) : undefined,
        onChange: onChangeTime,
        hideTimeZone: true,
        isDisabled: disabled,
        isReadOnly: readOnly,
        label
    }, ariaState, inputRef);
    /**
     * Manually clear the aria time value when our input controlled value has changed from a Date to undefined.
     * This is because the react aria hooks do not update the value once a time has been set.
     */ useEffect(()=>{
        if (value === undefined && ariaState.value !== null) {
            ariaState.clearSegment("hour");
            ariaState.clearSegment("minute");
            ariaState.clearSegment("second");
        }
    }, [
        ariaState,
        value
    ]);
    return React__default.createElement(Stack, {
        "data-hd-time-input": true,
        "data-hd-disabled": disabled,
        "data-hd-readonly": readOnly,
        "data-hd-error": error === true || typeof error === "string" && error.length > 0,
        gap: 0.5
    }, React__default.createElement(Input, {
        ref: inputRef,
        "data-test-id": `TimeInput-${testId}`,
        ...props,
        ...ariaTime.labelProps,
        onBlur: onBlurTime
    }, React__default.createElement(InputInnerText, {
        ...ariaTime.fieldProps
    }, ariaState.segments.map((segment, i)=>React__default.createElement(InputSegment, {
            key: i,
            segment: segment,
            state: ariaState
        }))), React__default.createElement(InnerInputLabel, {
        className: "time-input-inner-label",
        size: "small"
    }, label), value !== undefined && !disabled && !readOnly && React__default.createElement(ClearButton, {
        testId: `TimeInput-${testId}-Clear`,
        icon: "close",
        size: "small",
        variant: "default",
        onClick: ()=>onChangeTime(undefined)
    }), React__default.createElement(__Icon, {
        className: "time-input-icon",
        icon: "clock"
    })), React__default.createElement(InputDescription, {
        reserveMessageSpace: reserveMessageSpace,
        error: typeof error === "string" ? error : undefined,
        description: description
    }));
};
TimeInput.dataId = "data-hd-time-input";
TimeInput.displayName = "TimeInput";

export { TimeInput };
